// components/AddLoanPaymentModal.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { formatCurrency } from '../utils';
import { showToast } from './Toast';

const AddLoanPaymentModal = ({ debt, accounts, onClose, onSave }) => {
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [principalAmount, setPrincipalAmount] = useState('');
  const [interestAmount, setInterestAmount] = useState('');
  const [escrowAmount, setEscrowAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [autoCalculate, setAutoCalculate] = useState(true);

  const isMortgage = debt?.type === 'mortgage';

  // Calculate estimated payment based on APR
  useEffect(() => {
    if (autoCalculate && debt?.apr && debt?.current_balance && paymentAmount) {
      const monthlyRate = (debt.apr / 100) / 12;
      const totalPayment = parseFloat(paymentAmount) || 0;
      const currentBalance = debt.current_balance || 0;

      // Calculate interest portion (balance * monthly rate)
      const calculatedInterest = currentBalance * monthlyRate;

      // Principal is the remainder after interest (and escrow if mortgage)
      let calculatedPrincipal = totalPayment - calculatedInterest;

      // For mortgages, if escrow is set, subtract it from principal
      if (isMortgage && escrowAmount) {
        calculatedPrincipal = totalPayment - calculatedInterest - parseFloat(escrowAmount);
      }

      setInterestAmount(calculatedInterest.toFixed(2));
      setPrincipalAmount(Math.max(0, calculatedPrincipal).toFixed(2));
    }
  }, [paymentAmount, escrowAmount, autoCalculate, debt?.apr, debt?.current_balance, isMortgage]);

  // Set default payment account to first active account
  useEffect(() => {
    if (accounts.length > 0 && !paymentAccountId) {
      setPaymentAccountId(accounts[0].id);
    }
  }, [accounts, paymentAccountId]);

  const handlePaymentAmountChange = (value) => {
    setPaymentAmount(value);
    if (!autoCalculate) {
      setAutoCalculate(true);
    }
  };

  const handleManualEdit = () => {
    setAutoCalculate(false);
  };

  const handleSave = async () => {
    try {
      // Validate inputs
      if (!paymentDate || !paymentAmount || !paymentAccountId) {
        showToast('Please fill in all required fields', 'error');
        return;
      }

      const totalPayment = parseFloat(paymentAmount);
      const principal = parseFloat(principalAmount) || 0;
      const interest = parseFloat(interestAmount) || 0;
      const escrow = parseFloat(escrowAmount) || 0;

      // Validate amounts add up
      const sum = principal + interest + escrow;
      if (Math.abs(sum - totalPayment) > 0.01) {
        showToast('Principal + Interest + Escrow must equal total payment amount', 'error');
        return;
      }

      // Create the payment transaction
      const transactionPayload = {
        account_id: paymentAccountId,
        type: 'payment',
        amount: totalPayment,
        date: paymentDate,
        description: `${debt.type === 'mortgage' ? 'Mortgage' : 'Loan'} Payment to ${debt.name}`,
        debt_id: debt.id,
        principal_amount: principal,
        interest_amount: interest,
        escrow_amount: escrow,
        notes: notes.trim() || null,
        is_reconciled: false
      };

      await axios.post(`${API_BASE_PATH}/transactions`, transactionPayload);

      showToast('Payment recorded successfully', 'success');
      onSave();
    } catch (error) {
      console.error('Error recording payment:', error);
      showToast('Failed to record payment', 'error');
    }
  };

  const totalEntered = (parseFloat(principalAmount) || 0) +
                       (parseFloat(interestAmount) || 0) +
                       (parseFloat(escrowAmount) || 0);
  const paymentTotal = parseFloat(paymentAmount) || 0;
  const isValid = Math.abs(totalEntered - paymentTotal) < 0.01;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>
            <i className="fas fa-money-check-alt" style={{ marginRight: '12px', color: 'var(--primary-color)' }}></i>
            Record {debt?.type === 'mortgage' ? 'Mortgage' : 'Loan'} Payment
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {/* Loan Summary */}
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--border-radius)',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-primary)' }}>
              {debt?.name}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
              <div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '4px' }}>Current Balance</div>
                <div style={{ fontWeight: 600, fontSize: '16px' }}>{formatCurrency(debt?.current_balance || 0)}</div>
              </div>
              <div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '4px' }}>APR</div>
                <div style={{ fontWeight: 600, fontSize: '16px' }}>{debt?.apr || 0}%</div>
              </div>
              {isMortgage && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '4px' }}>Escrow Balance</div>
                  <div style={{ fontWeight: 600, fontSize: '16px' }}>{formatCurrency(debt?.escrow_balance || 0)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Details */}
          <div className="form-group">
            <label className="form-label">Payment Date *</label>
            <input
              type="date"
              className="form-control"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Payment Account *</label>
            <select
              className="form-control"
              value={paymentAccountId}
              onChange={(e) => setPaymentAccountId(e.target.value)}
            >
              <option value="">Select account...</option>
              {accounts.filter(a => a.is_active).map(account => (
                <option key={account.id} value={account.id}>
                  {account.name} - {formatCurrency(account.current_balance)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Total Payment Amount *</label>
            <input
              type="number"
              className="form-control"
              value={paymentAmount}
              onChange={(e) => handlePaymentAmountChange(e.target.value)}
              placeholder="0.00"
              step="0.01"
            />
          </div>

          {/* Payment Breakdown */}
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--border-radius)',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>Payment Breakdown</span>
              {!isValid && paymentAmount && (
                <span style={{ fontSize: '11px', color: 'var(--danger-color)', fontWeight: 400 }}>
                  Total must equal {formatCurrency(paymentTotal)}
                </span>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label">Principal Amount</label>
              <input
                type="number"
                className="form-control"
                value={principalAmount}
                onChange={(e) => {
                  setPrincipalAmount(e.target.value);
                  handleManualEdit();
                }}
                placeholder="0.00"
                step="0.01"
              />
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label">Interest Amount</label>
              <input
                type="number"
                className="form-control"
                value={interestAmount}
                onChange={(e) => {
                  setInterestAmount(e.target.value);
                  handleManualEdit();
                }}
                placeholder="0.00"
                step="0.01"
              />
            </div>

            {isMortgage && (
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label">Escrow Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={escrowAmount}
                  onChange={(e) => setEscrowAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            )}

            <div style={{
              fontSize: '12px',
              color: 'var(--text-secondary)',
              fontStyle: 'italic',
              marginTop: '8px',
              padding: '8px',
              background: 'var(--bg-tertiary)',
              borderRadius: '4px'
            }}>
              {autoCalculate ? (
                <>
                  <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                  Auto-calculated based on APR. Edit any field to customize.
                </>
              ) : (
                <>
                  <i className="fas fa-edit" style={{ marginRight: '6px' }}></i>
                  Manual entry mode. Amounts must add up to total payment.
                </>
              )}
            </div>

            {/* Total Verification */}
            <div style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '13px',
              fontWeight: 600
            }}>
              <span>Total Entered:</span>
              <span style={{ color: isValid ? 'var(--success-color)' : 'var(--danger-color)' }}>
                {formatCurrency(totalEntered)}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes (Optional)</label>
            <textarea
              className="form-control"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this payment..."
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!isValid || !paymentAmount || !paymentAccountId}
              style={{ flex: 1 }}
            >
              <i className="fas fa-check" style={{ marginRight: '8px' }}></i>
              Record Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLoanPaymentModal;
